import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { DraggableCore } from 'react-draggable';
import throttle from 'lodash.throttle';
import GradientPreview from '../gradient-preview';
import { normalizePosition } from './helpers/normalize-position';
import { getPercentPosition } from './helpers/get-percent-position';
import { getNextPositionByPressedKey } from './helpers/get-next-position-by-pressed-key';

const DRAG_THROTTLE_MS = 50;

const getClientCoords = (event, data, rect, touchListKey = 'touches') => {
  if ('clientX' in event && 'clientY' in event) {
    return {
      clientX: event.clientX,
      clientY: event.clientY,
    };
  }

  if (touchListKey in event) {
    const touchList = event[touchListKey];
    const touch = touchList?.[0];

    if (touch) {
      return {
        clientX: touch.clientX,
        clientY: touch.clientY,
      };
    }
  }

  if (rect) {
    return {
      clientX: rect.left + data.x,
      clientY: rect.top + data.y,
    };
  }

  return null;
};

const RadialGradientCenterPicker = ({
  position,
  background,
  onDragStart = undefined,
  onDrag = undefined,
  onDragEnd = undefined,
  onClick = undefined,
  onKeyboardChange = undefined,
}) => {
  const gradientCanvasRef = useRef(null);
  const handleRef = useRef(null);
  const ignoreNextClickRef = useRef(false);
  const lastDragClientCoordsRef = useRef(null);

  const currentPosition = normalizePosition(position);

  const onDragRef = useRef(onDrag);
  onDragRef.current = onDrag;

  const emitPosition = useCallback((nextPosition, callback) => {
    callback?.({ position: normalizePosition(nextPosition) });
  }, []);

  // Stable throttled function — reads onDrag via ref so it never needs to be recreated
  // when the callback identity changes, preserving in-flight throttle state across renders.
  const throttledDrag = useMemo(
    () =>
      throttle((next) => {
        emitPosition(next, onDragRef.current);
      }, DRAG_THROTTLE_MS),
    [emitPosition]
  );

  const getPositionFromCoords = useCallback((clientX, clientY) => {
    // Read the ref directly here so this callback does not depend
    // on a helper function recreated on each render.
    const rect = gradientCanvasRef.current?.getBoundingClientRect() ?? null;

    return rect ? getPercentPosition(clientX, clientY, rect) : null;
  }, []);

  const handleClick = useCallback(
    (event) => {
      if (ignoreNextClickRef.current) {
        ignoreNextClickRef.current = false;

        return;
      }

      let clientX;
      let clientY;

      if ('touches' in event && event.touches.length > 0) {
        const { clientX: touchX, clientY: touchY } = event.touches[0];

        clientX = touchX;
        clientY = touchY;
      } else if ('clientX' in event) {
        const { clientX: mouseX, clientY: mouseY } = event;

        clientX = mouseX;
        clientY = mouseY;
      }

      if (clientX != null && clientY != null) {
        const nextPosition = getPositionFromCoords(clientX, clientY);

        if (nextPosition) {
          emitPosition(nextPosition, onClick);
        }
      }
    },
    [emitPosition, getPositionFromCoords, onClick]
  );

  const handleDrag = useCallback(
    (event, data) => {
      const coords = getClientCoords(
        event,
        data,
        gradientCanvasRef.current?.getBoundingClientRect() ?? null
      );

      if (coords) {
        lastDragClientCoordsRef.current = coords;
        const nextPosition = getPositionFromCoords(coords.clientX, coords.clientY);

        if (nextPosition) {
          throttledDrag(nextPosition);
        }
      }
    },
    [getPositionFromCoords, throttledDrag]
  );

  const handleDragStart = useCallback(
    (event, data) => {
      ignoreNextClickRef.current = true;

      const coords = getClientCoords(
        event,
        data,
        gradientCanvasRef.current?.getBoundingClientRect() ?? null
      );

      if (coords) {
        lastDragClientCoordsRef.current = coords;
        const nextPosition = getPositionFromCoords(coords.clientX, coords.clientY);

        if (nextPosition) {
          emitPosition(nextPosition, onDragStart);
        }
      }
    },
    [emitPosition, getPositionFromCoords, onDragStart]
  );

  const handleDragStop = useCallback(
    (event, data) => {
      throttledDrag.cancel();

      ignoreNextClickRef.current = false;

      const coords =
        getClientCoords(event, data, null, 'changedTouches') ?? lastDragClientCoordsRef.current;

      lastDragClientCoordsRef.current = null;

      if (coords) {
        const nextPosition = getPositionFromCoords(coords.clientX, coords.clientY);

        if (nextPosition) {
          emitPosition(nextPosition, onDragEnd);
        }
      }
    },
    [emitPosition, getPositionFromCoords, onDragEnd, throttledDrag]
  );

  const handleKeyDown = useCallback(
    (event) => {
      const nextPosition = getNextPositionByPressedKey(event.key, currentPosition);

      if (nextPosition) {
        event.preventDefault();
        emitPosition(nextPosition, onKeyboardChange);
      }
    },
    [currentPosition, emitPosition, onKeyboardChange]
  );

  useEffect(
    () => () => {
      throttledDrag.cancel();
    },
    [throttledDrag]
  );

  return (
    <div
      className="ws-ui-radial-gradient-center-picker"
      onMouseDown={handleClick}
      onTouchStart={handleClick}
    >
      <GradientPreview ref={gradientCanvasRef} background={background} />
      <DraggableCore
        onStart={handleDragStart}
        onDrag={handleDrag}
        onStop={handleDragStop}
        nodeRef={handleRef}
      >
        <button
          ref={handleRef}
          type="button"
          className="ws-ui-radial-gradient-center-picker__handle"
          style={{
            '--radial-gradient-center-x': `${currentPosition.x}%`,
            '--radial-gradient-center-y': `${currentPosition.y}%`,
          }}
          onKeyDown={handleKeyDown}
        />
      </DraggableCore>
    </div>
  );
};

RadialGradientCenterPicker.displayName = 'RadialGradientCenterPicker';

export default RadialGradientCenterPicker;
